/* eslint-disable @typescript-eslint/no-non-null-assertion */
// eslint-disable-next-line
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import '../css/dialog.css';

import { AppContext } from '../AppContext';
import Container from '../basicComponents/Container';
import Text from '../basicComponents/Text';
import LogRecord from '../data/LogRecord';
import SessionHeader from '../appComponents/SessionHeader';
import { RecordNavigation } from '../App';
import TextClick from '../basicComponents/TextClick';
import Select from '../basicComponents/Select';

interface Recording {
    sessionId: string;
    turn: number;
    mode: string;
    url: string;
    transcription: string;
}

interface DialogLine {
    sessionId: string;
    turn: number;
    user: string | undefined;
    userRawUtterance: string | undefined;
    userConfidence: string | undefined;
    userRecording: Recording | undefined;
    system: string | undefined;
    systemRecording: Recording | undefined;
}

interface GetDialogResponse {
    lines: DialogLine[];
}

interface TestcaseRequest {
    sessionId: string;
    turn: number;
    testcase: string;
}

export interface DialogPageProps extends RouteComponentProps { 
    curRecord: LogRecord | undefined;
    recordNavigation: RecordNavigation;
    setCurRecord: (record: LogRecord) => void;
}

export interface DialogPageState { 
    lines: DialogLine[];
    playStatus: Map<string, number>;
    testcaseIdentifier: string;
}

class DialogPageComponent extends React.Component<DialogPageProps, DialogPageState> {

    // app context
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    private sessionId: string;

    private iconMap: Map<number, JSX.Element>;
    private audio: HTMLAudioElement | undefined; 
    private curAudio: string | undefined;

    private recordNavigation: RecordNavigation;

    constructor(props: DialogPageProps) {

        super(props);

        const path = this.props.history.location.pathname.split("/");

        this.sessionId = path[path.length - 1];

        this.iconMap = new Map<number, JSX.Element>();
        this.iconMap.set(0, <i className="fas fa-volume-down"></i>);
        this.iconMap.set(1, <i className="fas fa-pause"></i>);
        this.iconMap.set(2, <i className="fas fa-play"></i>);

        this.audio = undefined;

        this.recordNavigation = { 
            ...this.props.recordNavigation,
            moveToRecord: this.moveToRecord,
        };

        this.state = {
            lines: [],
            playStatus: new Map<string, number>(),
            testcaseIdentifier: '<testcase identifier>',
        };

    }

    public shouldComponentUpdate(): boolean {

        // split path
        const path = this.props.history.location.pathname.split("/");
        const curSessionId = path[path.length - 1];

        // check if session changed
        if (this.sessionId !== curSessionId) {

            this.sessionId = curSessionId;
            this.setState({ lines: [] });

            // get dialog
            this.context.restAppService.request_get('dialog/record/' + this.sessionId, this.context.user.getToken(),
                (data): void => {

                    // get lines
                    const response = JSON.parse(data) as GetDialogResponse;

                    // set playStatus
                    const playStatus = new Map<string, number>();
                    response.lines.forEach( line => {
                        if (line.systemRecording !== undefined) { playStatus.set(this.getRecordingString(line.systemRecording), 0) }
                        if (line.userRecording   !== undefined) { playStatus.set(this.getRecordingString(line.userRecording),   0) }
                    })

                    // set state
                    this.setState({ lines: response.lines, playStatus });

                    // set current record
                    this.context.app.setCurRecordFromServerSession(curSessionId);

                },
                (status, text): void => {
                    console.log(`request failure ${status} ${text}`)
                }
            );

        }
        return true;
    }

    public render(): JSX.Element {

		if (this.props.curRecord === undefined) {
			return <div />
		}
        
        return (
            <Container className='dialog-page'>
                <Container className='session-line'>
                    <SessionHeader curRecord={this.props.curRecord} recordNavigation={this.recordNavigation} />
                    <div className='session-space' />
                    <Select className='session-select' name='testcaseIdentifier' value={this.state.testcaseIdentifier} options={this.context.app.getTestcaseList()} onChange={this.handleTestcaseChange} />
                    <TextClick className='session-button-group' name='clipboard' onClick={this.handleButtonClick}>Copy SessionId</TextClick>
                    <TextClick className='session-button-group' name='history' onClick={this.handleButtonClick}>History</TextClick>
                    <TextClick className='session-button' name='logging' onClick={this.handleButtonClick}>Logging</TextClick>
                </Container>
                <Container className='dialog-list'>
                    {this.state.lines.map( (line, idx) => {

                        // check for transcription
                        let userText: JSX.Element = <div />
                        if (line.user !== undefined) {
                            if (line.userRawUtterance || (line.userRecording && line.userRecording.transcription)) {
                                userText = <span className='dialog-user-value'>
                                        {line.user}
                                        <i>  {line.userRawUtterance ? line.userRawUtterance : null}</i>
                                        <i> ({line.userConfidence ? line.userConfidence : null})</i>
                                        {line.userRecording && line.userRecording.transcription ?
                                            <i><br />{line.userRecording.transcription}</i>
                                            : 
                                            null}
                                    </span>
                            }
                            else {
                                userText = <Text className='dialog-user-value'>{line.user}</Text>
                            }
                        }

                        return (
                            <React.Fragment key={idx}>
                                {line.system !== undefined ? <Text className='dialog-system-value'>{line.system}</Text> : <div />}
                                {line.systemRecording !== undefined ? <span className='dialog-system-recording' data-name={this.getRecordingString(line.systemRecording)} onClick={this.handleAudioClick}>{this.iconMap.get(this.state.playStatus.get(this.getRecordingString(line.systemRecording))!)}</span> : <div />}
                                {line.userRecording   !== undefined ? <span className='dialog-user-recording'   data-name={this.getRecordingString(line.userRecording)}   onClick={this.handleAudioClick}>{this.iconMap.get(this.state.playStatus.get(this.getRecordingString(line.userRecording))!)}</span> : <div />}
                                {userText}
                                {line.system === undefined ?  
                                    this.state.testcaseIdentifier !== '<testcase identifier>' ? 
                                        <div className='dialog-user-testcase' data-name={line.sessionId + '|' + line.turn} onClick={this.handleTestcaseClick}><i className="fas fa-plus"></i></div > : 
                                        <div />
                                    :
                                    <div />
                                }
                            </React.Fragment>
                        );

                    })}
                </Container>
            </Container>
            
        );

    }

    public componentDidMount(): void {

        // get log record if necessary
        if (this.props.curRecord === undefined) {

            this.context.restAppService.request_get('logRecord/request/' + this.sessionId, this.context.user.getToken(),
                (data): void => {

                    // get response
                    const response = JSON.parse(data) as LogRecord;

                    // set curRecord
                    this.props.setCurRecord(response);

                    // set sidebar
                    this.context.app.addSidebar({ name: 'DLG ' + this.sessionId, link: '/dialog/' + this.sessionId });      

                },
                (status, text) => {
                    console.log(`request failure ${status} ${text}`)
                }
            );
        }

        // get logHistory
        this.context.restAppService.request_get('dialog/record/' + this.sessionId, this.context.user.getToken(),
            (data): void => {

                // get lines
                const response = JSON.parse(data) as GetDialogResponse;

                // set playStatus
                const playStatus = new Map<string, number>();
                response.lines.forEach( line => {
                    if (line.systemRecording !== undefined) { playStatus.set(this.getRecordingString(line.systemRecording), 0) }
                    if (line.userRecording   !== undefined) { playStatus.set(this.getRecordingString(line.userRecording),   0) }
                })

                // set state
                this.setState({ lines: response.lines, playStatus });

            },
            (status, text): void => {
                console.log(`request failure ${status} ${text}`)
            }
        );

    }

    public moveToRecord = (nextRecord: LogRecord): void => {

        console.log('move to next record ' + nextRecord.sessionId);

        if (this.props.curRecord) {

            // replace entry in sidebar
            this.context.app.replaceSidebar(
                { name: 'DLG ' + this.props.curRecord.sessionId, link: '/dialog/' + this.props.curRecord.sessionId }, 
                { name: 'DLG ' + nextRecord.sessionId, link: '/dialog/' + nextRecord.sessionId }
                );

            // navigate to new index
            this.props.setCurRecord(nextRecord);
            this.props.history.push('/dialog/' + nextRecord.sessionId);

        }

    }

    private handleAudioClick = (event: React.MouseEvent<HTMLSpanElement>): void => {

        const target = event.currentTarget;
        const name = target.dataset.name;

        console.log('clicked on ' + name);

        if (name) {

            // check if the same audio button was clicked before
            if (this.audio && this.curAudio && this.curAudio === name) {

                // check status playing
                if (this.state.playStatus.get(name) === 1) {

                    // set new status
                    const playStatus = new Map<string, number>();
                    this.state.lines.forEach(line => {
                        if (line.systemRecording !== undefined) { playStatus.set(this.getRecordingString(line.systemRecording), 0) }
                        if (line.userRecording   !== undefined) { playStatus.set(this.getRecordingString(line.userRecording),   0) }
                    })
                    playStatus.set(name, 2);
                    this.setState({ playStatus });

                    // pause audio
                    this.audio.pause();

                }
                // check status paused
                else if (this.state.playStatus.get(name) === 2) {

                    // set new status
                    const playStatus = new Map<string, number>();
                    this.state.lines.forEach(line => {
                        if (line.systemRecording !== undefined) { playStatus.set(this.getRecordingString(line.systemRecording), 0) }
                        if (line.userRecording   !== undefined) { playStatus.set(this.getRecordingString(line.userRecording),   0) }
                    })
                    playStatus.set(name, 1);
                    this.setState({ playStatus });

                    // restart audio
                    this.audio.play();
                    
                }
                else {
                    console.log("this should not happen");
                }
            }
            else {

                // stop playing if player is active
                if (this.audio !== undefined) {
                    this.audio.pause();
                    this.audio = undefined;
                }
    
                // create new url
                let url;
                // Signal Iduna - s3Archive
                if (name.startsWith("https://nlu-aufzeichnungen-prod.s3.storage")) {
                    url = name;
                }
                // Signal Iduna - ITS Dialog
                else if (name.startsWith("http://app")) {
                    const path = name.split("/");
                    const fileName = path[path.length - 1];
                    url = "/SymQuery/audio/its/" + fileName;
                }
                // Sympalog Standard
                else if (window.location.port !== '3000') {
                    url = "/SymQuery/audio/" + name;
                }
                // Sympalog React Developtment Server
                else {
                    url = "http://localhost:14015/SymQuery/audio/" + this.sessionId + "/" + name;
                }

                // create audio and start playing
                this.audio = new Audio(url);
                this.audio.play();
                this.curAudio = name;

                // set play status
                const playStatus = new Map<string, number>();
                this.state.lines.forEach(line => {
                    if (line.systemRecording !== undefined) { playStatus.set(this.getRecordingString(line.systemRecording), 0) }
                    if (line.userRecording   !== undefined) { playStatus.set(this.getRecordingString(line.userRecording),   0) }
                })
                playStatus.set(name, 1);
                this.setState({ playStatus });

                // set on end handler
                this.audio.onended = () => { 
                    this.audio = undefined; 
                    this.curAudio = undefined;
                    const playStatus = new Map<string, number>();
                    this.state.lines.forEach(line => {
                        if (line.systemRecording !== undefined) { playStatus.set(this.getRecordingString(line.systemRecording), 0) }
                        if (line.userRecording   !== undefined) { playStatus.set(this.getRecordingString(line.userRecording),   0) }
                    })
                    this.setState({ playStatus });
                }

            }

        }

    }

    private handleButtonClick = (event: React.MouseEvent<HTMLSpanElement>): void => {

        const target = event.currentTarget;
        const name = target.dataset.name;

        switch (name) {
            case 'history':
                this.context.app.addSidebar({ name: 'HTY ' + this.sessionId, link: '/logHistory/' + this.sessionId });
                this.props.history.push('/logHistory/' + this.sessionId);
                break;
            case 'logging': 
                this.context.app.addSidebar({ name: 'LOG ' + this.sessionId, link: '/logging/' + this.sessionId });
                this.props.history.push('/logging/' + this.sessionId);
                break;
            case 'clipboard': 
                navigator.clipboard.writeText(`[Session: ${this.sessionId}](${window.location})`)
                .then(() => {
                    this.context.app.setStatus("copied sessionId to clipboard");
                }, (err) => {
                    this.context.app.setStatus("Error: could not copy sessionId to clipboard " + err);
                });
                break;
        }

    }

    private getRecordingString = (recording: Recording): string => {
        return recording.url ? recording.url : recording.sessionId + "/" + recording.turn + "/" + recording.mode;
    }

    private handleTestcaseChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {

        const target = event.currentTarget;
        const value = target.value;

        this.setState( {testcaseIdentifier: value });
        
    }

    private handleTestcaseClick = (event: React.MouseEvent<HTMLSpanElement>): void => {

        const target = event.currentTarget;
        const name = target.dataset.name;

        if (name !== undefined) {

            const idx = name.indexOf('|');
            const sessionId = name.substring(0, idx);
            const turn = Number.parseInt(name.substring(idx + 1));

            // build request
            const request: TestcaseRequest = {
                sessionId: sessionId,
                turn: turn,
                testcase: this.state.testcaseIdentifier
            };

            // send request
            this.context.restAppService.request_put('dialog/testcase', this.context.user.getToken(), 'text/json', JSON.stringify(request),
                (): void => {
                    this.context.app.setStatus("testcase saved");
                },
                (status, text): void => {
                    console.log("search failed " + status + " " + text);
                    this.context.app.setStatus("testcase save failed");
                }
            );

        }
        

        console.log('utterance ' + name + ' clicked');

    }

}

const DialogPage = withRouter(DialogPageComponent);
export default DialogPage;
